/**
 * Your account: sign in with an emailed code, and your one life is kept on it (play on any device). A guest plays
 * without one, saved in this browser only; signing up links the guest identity the game made on its first visit (the
 * same player to everyone you've met), it never starts a new life.
 *
 * The save is the game's own saved keys. Each push carries the revision this device last had; the server refuses one
 * that's behind (another device played on since), and the game loads the newer life instead of overwriting it.
 *
 * What you buy (a car, a helicopter, a home abroad, anything kept in `owned`) goes to the account straight away, not
 * at the next minute's save. And when the newer life from another device comes in, what THIS device bought since its
 * last save to the account comes with it (paid for, as it was): a helicopter bought on the phone just before it went
 * in a pocket used to be lost the moment the laptop's life replaced the phone's.
 */
import { SLOT, slotKey } from '../slot';
import { mpUrl, playerId } from './presence';
import { track } from './telemetry';
import { inboxKey } from './inbox';
import { AIRCRAFT } from '@sim';

const ACCOUNT_KEY = 'unfold.account';
/** what a life is, in storage (not the photos: they stay on the device for now) */
const SAVE_KEYS = ['lifesim.character.v1', 'lifesim.life.v1', 'lifesim.name.v1', 'lifesim.region', 'lifesim.state.v1', 'lifesim.visit', 'lifesim.seenApps'];

interface Stored { email: string; token: string; rev: number }
export interface SaveMeta { rev: number; at: number; device: string; bytes: number }

/** the server's http address (the multiplayer server's), or null: no accounts here */
function api(): string | null {
  const u = mpUrl();
  return u ? u.replace(/^ws/, 'http').replace(/\/$/, '') : null;
}
/** the server's http address, for a public read (GET) */
export const apiBase = () => api();
/** accounts work here (a server to keep them on, and not a test slot) */
export const accountsOn = () => !!api() && !SLOT; // (a dev test slot never touches the real account)

export function account(): Stored | null {
  if (SLOT) return null;
  try { const a = JSON.parse(localStorage.getItem(ACCOUNT_KEY) ?? 'null'); return a?.email && a?.token ? a : null; } catch { return null; }
}
function keep(a: Stored | null) { try { if (a) localStorage.setItem(ACCOUNT_KEY, JSON.stringify(a)); else localStorage.removeItem(ACCOUNT_KEY); } catch { /* storage blocked */ } }

/** this device, to the server (a stale push from this one is told apart from another device's) */
function device(): string {
  try { let d = localStorage.getItem('unfold.device'); if (!d) { d = crypto.randomUUID(); localStorage.setItem('unfold.device', d); } return d; } catch { return 'unknown'; }
}

async function post<T>(path: string, body: unknown, keepalive = false): Promise<{ ok: boolean; status: number; data: T & { error?: string } }> {
  const base = api();
  if (!base) return { ok: false, status: 0, data: { error: 'No server to sign in to.' } as T & { error?: string } };
  try {
    const r = await fetch(`${base}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive });
    return { ok: r.ok, status: r.status, data: await r.json() };
  } catch { return { ok: false, status: 0, data: { error: "Can't reach the server. Check your connection." } as T & { error?: string } }; }
}

/** a call to the account server (pay.ts) */
export const apiPost = <T>(path: string, body: unknown) => post<T>(path, body);

// --- what you own, and what this device bought since the account last had its life ------------------------------

/** a life's possessions, as the save has them */
interface Assets { owned: string[]; cars: { id: string; paid: number }[]; abroad: { id: string; price: number }[] }
/** the possessions in the account's copy, as of this device's last save to it or load from it */
const BASE_KEY = 'unfold.syncedAssets';
function assetsOf(stateJson: string | null | undefined): Assets | null {
  try {
    const s = JSON.parse(stateJson ?? 'null') as { owned?: string[]; vehicles?: { id: string; paid?: number }[]; homesAbroad?: { id: string; price?: number; kept?: boolean }[] } | null;
    if (!s || !Array.isArray(s.owned)) return null;
    return { owned: [...s.owned], cars: (s.vehicles ?? []).map((v) => ({ id: v.id, paid: v.paid ?? 0 })), abroad: (s.homesAbroad ?? []).filter((h) => !h.kept).map((h) => ({ id: h.id, price: h.price ?? 0 })) };
  } catch { return null; }
}
function setBaseline(stateJson: string | null | undefined) { try { const a = assetsOf(stateJson); if (a) localStorage.setItem(BASE_KEY, JSON.stringify(a)); else localStorage.removeItem(BASE_KEY); } catch { /* storage blocked */ } }
function baseline(): Assets | null { try { return JSON.parse(localStorage.getItem(BASE_KEY) ?? 'null'); } catch { return null; } }
/** a short fingerprint of what's owned (the game watches it: a purchase saves to the account at once) */
export function ownedSig(s: { owned: string[]; vehicles?: { id: string }[]; homesAbroad?: { id: string }[]; card?: { home?: { id: string } } }): string {
  return `${s.owned.length}:${s.owned.slice(-3).join(',')}|${(s.vehicles ?? []).map((v) => v.id).join(',')}|${(s.homesAbroad ?? []).map((h) => h.id).join(',')}|${s.card?.home?.id ?? ''}`;
}
/**
 * The account's life is coming in over this one: what this device bought since its last save to the account (in its
 * life, not in the baseline) goes into the incoming life too, paid for as it was. (Only additions: what was sold here
 * since stays sold only if the other device sold it too.) Returns the incoming state with them, or null: nothing to add.
 */
function carryPurchases(mineJson: string | null, incomingJson: string | undefined): string | null {
  const mine = assetsOf(mineJson), base = baseline();
  if (!mine || !base || !incomingJson) return null;
  let s: { owned: string[]; vehicles?: unknown[]; homesAbroad?: unknown[]; money: number; minutes: number; ledger: { t: number; amount: number; label: string }[]; history: { t: number; text: string }[] };
  let mineState: { vehicles?: { id: string }[]; homesAbroad?: { id: string }[] };
  try { s = JSON.parse(incomingJson); mineState = JSON.parse(mineJson!); } catch { return null; }
  if (!s || !Array.isArray(s.owned)) return null;
  const had = (x: string) => base.owned.includes(x);
  let added = 0;
  const pay = (amount: number, label: string) => { if (amount > 0) { s.money -= amount; (s.ledger ??= []).push({ t: s.minutes, amount: -amount, label }); } };
  for (const o of mine.owned) if (!had(o) && !s.owned.includes(o)) {
    s.owned.push(o); added++;
    const a = /^aircraft:(\w+)$/.exec(o)?.[1] as keyof typeof AIRCRAFT | undefined;
    if (a && AIRCRAFT[a]) pay(AIRCRAFT[a].price, `${AIRCRAFT[a].name} (${AIRCRAFT[a].kind}), bought on another device`);
  }
  for (const c of mine.cars) if (!base.cars.some((x) => x.id === c.id) && !(s.vehicles ?? []).some((x) => (x as { id: string }).id === c.id)) {
    const v = mineState.vehicles?.find((x) => x.id === c.id);
    if (v) { (s.vehicles ??= []).push(v); added++; pay(c.paid, 'A car, bought on another device'); }
  }
  for (const h of mine.abroad) if (!base.abroad.some((x) => x.id === h.id) && !(s.homesAbroad ?? []).some((x) => (x as { id: string }).id === h.id)) {
    const v = mineState.homesAbroad?.find((x) => x.id === h.id);
    if (v) { (s.homesAbroad ??= []).push(v); added++; pay(h.price, 'A home abroad, bought on another device'); }
  }
  if (!added) return null;
  (s.history ??= []).push({ t: s.minutes, text: `Kept ${added === 1 ? 'a purchase' : `${added} purchases`} made on another device.` });
  return JSON.stringify(s);
}

function snapshot(): Record<string, string> {
  const out: Record<string, string> = {};
  try { for (const k of SAVE_KEYS) { const v = localStorage.getItem(slotKey(k)); if (v !== null) out[k] = v; } } catch { /* storage blocked */ }
  return out;
}
function restore(data: Record<string, string> | null) {
  // (the running game stops saving: its life in memory is the old one, and would be written back on the way out)
  dispatchEvent(new Event('unfold:life-replaced'));
  try {
    for (const k of SAVE_KEYS) localStorage.removeItem(slotKey(k));
    if (data) for (const [k, v] of Object.entries(data)) if (SAVE_KEYS.includes(k)) localStorage.setItem(slotKey(k), v);
    localStorage.removeItem(slotKey('lifesim.arrive'));
  } catch { /* storage blocked */ }
}
const hasLife = () => { try { return !!localStorage.getItem(slotKey('lifesim.life.v1')); } catch { return false; } };

/** Step one: a code to this address. (A local server hands it back too: `devCode`.) */
export async function askCode(email: string): Promise<{ error?: string; devCode?: string }> {
  const r = await post<{ devCode?: string }>('/auth/code', { email });
  return r.ok ? { devCode: r.data.devCode } : { error: r.data.error ?? 'Something went wrong.' };
}

/**
 * Step two: the code. Signed in → this device becomes the account's player (if the account already has a life, it's
 * loaded here: 'loaded'); a new account takes this guest's identity and this device's life ('saved', or 'empty' if
 * there's no life yet).
 */
export type SignedIn = 'loaded' | 'saved' | 'empty';
export async function signIn(email: string, code: string): Promise<{ error?: string; result?: SignedIn | 'both' }> {
  const id = playerId();
  const r = await post<{ token: string; id: string; key: string; save: SaveMeta | null }>('/auth/verify', { email, code, guest: { id, key: inboxKey(id) } });
  if (!r.ok) return { error: r.data.error ?? 'Something went wrong.' };
  return entered(r.data, email, id, 'code');
}

/** Sign in with Google: its token, from Google's own button (no code, no email sent) */
export async function signInGoogle(credential: string): Promise<{ error?: string; result?: SignedIn | 'both' }> {
  const id = playerId();
  const r = await post<{ token: string; id: string; key: string; save: SaveMeta | null; email: string }>('/auth/google', { credential, guest: { id, key: inboxKey(id) } });
  if (!r.ok) return { error: r.data.error ?? 'Something went wrong.' };
  return entered(r.data, r.data.email, id, 'google');
}

async function entered(d: { token: string; id: string; key: string; save: SaveMeta | null }, email: string, id: string, how: 'code' | 'google'): Promise<{ error?: string; result?: SignedIn | 'both' }> {
  const r = { data: d };
  const { token, save } = d;
  try {
    // the account's own identity (another device made it): this one becomes that player
    if (r.data.id !== id) { localStorage.setItem('unfold.playerId', r.data.id); localStorage.setItem(`unfold.playerKey.${r.data.id}`, r.data.key); sessionStorage.removeItem('unfold.pid'); }
  } catch { /* storage blocked */ }
  keep({ email: email.trim().toLowerCase(), token, rev: 0 });
  track('account_signed_in', { had_save: !!save, how }); // (never the email)
  // (the account has a life, and so does this device: one life per player, so you choose: `keepOne`)
  if (save && save.bytes > 0 && hasLife()) { keep({ ...account()!, rev: save.rev }); return { result: 'both' }; }
  if (save && save.bytes > 0) { await pull(); return { result: 'loaded' }; }
  if (save) keep({ ...account()!, rev: save.rev }); // (a life that was ended: count on from there)
  if (!hasLife()) return { result: 'empty' };
  const p = await push();
  return p === 'saved' ? { result: 'saved' } : { error: p === 'too big' ? "This life is too big to save online yet. It's still safe on this device." : "Signed in, but the save didn't go through. It'll try again." };
}

/** Both had a life: the account's onto this device ('account'), or this device's onto the account ('device'). */
export async function keepOne(which: 'account' | 'device'): Promise<SignedIn | null> {
  if (which === 'account') return (await pull()) ? 'loaded' : null;
  return (await push()) === 'saved' ? 'saved' : null;
}

/** The account's life onto this device (it replaces what's here). */
async function pull(): Promise<boolean> {
  const a = account();
  if (!a) return false;
  const r = await post<{ meta: SaveMeta | null; data?: Record<string, string> }>('/save/load', { email: a.email, token: a.token });
  if (r.status === 401) { keep(null); return false; }
  if (!r.ok) return false;
  const data = r.data.data ?? null;
  // (an answer with no life in it, when the account says it has one, isn't a life: never wipe this one with it)
  if (!data?.['lifesim.state.v1'] && (r.data.meta?.bytes ?? 0) > 0) return false;
  if (!data) return false;
  let mine: string | null = null;
  try { mine = localStorage.getItem(slotKey('lifesim.state.v1')); } catch { /* storage blocked */ }
  const merged = carryPurchases(mine, data['lifesim.state.v1']);
  if (merged) data['lifesim.state.v1'] = merged;
  restore(data);
  setBaseline(data['lifesim.state.v1']);
  keep({ ...a, rev: r.data.meta?.rev ?? a.rev });
  if (merged) { try { sessionStorage.setItem('unfold.carried', '1'); } catch { /* storage blocked */ } void push(); } // (the account has them too, now)
  return true;
}
/** the last load kept purchases from this device (once: the game says so) */
export function carried(): boolean { try { const c = sessionStorage.getItem('unfold.carried') === '1'; sessionStorage.removeItem('unfold.carried'); return c; } catch { return false; } }

/**
 * On the way in (before the game loads): if another device played on since this one last saved, take its life.
 * Quick, and never in the way: offline, the device's own life plays.
 */
export async function syncOnStart(): Promise<void> {
  const a = account();
  if (!a || !api()) return;
  const r = await Promise.race([post<{ meta: SaveMeta | null }>('/save/meta', { email: a.email, token: a.token }), new Promise<null>((ok) => setTimeout(() => ok(null), 3000))]);
  if (!r) return;
  if (r.status === 401) { keep(null); return; }
  const m = r.data.meta;
  if (!r.ok || !m || m.rev <= a.rev) return;
  if (m.bytes === 0) { restore(null); keep({ ...a, rev: m.rev }); return; } // (the life was ended on another device)
  await pull();
}

let pushing = false;
/** Keep this life on the account now. */
export async function push(keepalive = false): Promise<'saved' | 'behind' | 'too big' | 'failed' | 'off'> {
  const a = account();
  if (!a || !hasLife()) return 'off';
  if (pushing) return 'failed';
  pushing = true;
  try {
    const data = snapshot(), body = { email: a.email, token: a.token, base: a.rev, device: device(), data };
    // (keepalive carries at most 64 KB: a grown life's save, sent as the tab went away, failed every time. Bigger: an
    // ordinary request, which a closing tab may or may not finish; what's bought is already saved at the time)
    const r = await post<{ meta: SaveMeta }>('/save/store', body, keepalive && JSON.stringify(body).length < 60_000);
    if (r.status === 401) { keep(null); return 'off'; }
    if (r.status === 409) return (lastPush = 'behind');
    if (r.status === 413) return (lastPush = 'too big');
    if (!r.ok) return (lastPush = 'failed');
    keep({ ...a, rev: r.data.meta.rev });
    setBaseline(data['lifesim.state.v1']);
    lastSaved = r.data.meta.at;
    return (lastPush = 'saved');
  } finally { pushing = false; }
}
/** when this device last saved to the account (this session) */
export let lastSaved = 0;
/** how the last save to the account went (null: none yet this session) */
export let lastPush: 'saved' | 'behind' | 'too big' | 'failed' | null = null;

/** While playing: saved to the account every minute or so, and as the tab goes away. `behind`: another device played
 * on; the game loads that life (told first). */
export function autoSave(onBehind: () => void) {
  if (!account()) return;
  let busy = false;
  const go = async (keepalive = false) => {
    if (busy) return;
    busy = true;
    const r = await push(keepalive);
    busy = false;
    if (r === 'behind') onBehind();
  };
  setInterval(() => void go(), 60_000);
  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void go(true); });
  saveSoon = () => { void go(); };
}
/** something was bought: to the account now (set up by autoSave) */
export let saveSoon: () => void = () => {};

/** Load the newer life from the account and start again with it. */
export async function takeNewer() { if (await pull()) location.reload(); }

/** The life ended on purpose: it goes from the account too. */
export async function clearSave() {
  const a = account();
  if (!a) return;
  const r = await post<{ ok: boolean }>('/save/clear', { email: a.email, token: a.token });
  if (r.ok) keep({ ...a, rev: a.rev + 1 });
}

/** Sign out: this device forgets the account and the life on it (it's on the account; sign in to get it back). */
export async function signOut() {
  const a = account();
  if (!a) return;
  await push();
  await post('/auth/signout', { email: a.email, token: a.token });
  keep(null);
  restore(null);
}

// --- the sign-in sheet (the creator and the game both use it) -----------------------------------------------------

/**
 * Email → code → signed in. `intro`: what it's for, in a line. Resolves with the outcome (null: closed).
 */
export function signInSheet(title: string, intro: string): Promise<SignedIn | null> {
  ensureCss();
  return new Promise((done) => {
    const shade = document.createElement('div');
    shade.className = 'ac-shade';
    shade.innerHTML = `<div class="ac-sheet" role="dialog"><button class="ac-x" title="Close">×</button><h2></h2><p class="ac-intro"></p>
      <div class="ac-google" hidden><div class="ac-gbtn"></div><div class="ac-or"><span>or with your email</span></div></div>
      <form class="ac-step ac-email"><label>Your email<input type="email" autocomplete="email" required placeholder="you@example.com"></label><button class="ac-go">Send me a code</button></form>
      <form class="ac-step ac-code" hidden><label>The 6-digit code we emailed to <b></b><input inputmode="numeric" autocomplete="one-time-code" maxlength="6" required placeholder="123456"></label><button class="ac-go">Sign in</button><button type="button" class="ac-back">Use a different email</button></form>
      <p class="ac-err" hidden></p><p class="ac-small">No password. The code works once, for 10 minutes.</p></div>`;
    shade.querySelector('h2')!.textContent = title;
    shade.querySelector('.ac-intro')!.textContent = intro;
    document.body.appendChild(shade);
    const [fe, fc] = [...shade.querySelectorAll('form')] as HTMLFormElement[];
    const ie = fe.querySelector('input')!, ic = fc.querySelector('input')!, err = shade.querySelector('.ac-err') as HTMLElement;
    // (typing isn't walking: the game's keys stay out of it)
    for (const i of [ie, ic]) for (const t of ['keydown', 'keyup']) i.addEventListener(t, (e) => { if ((e as KeyboardEvent).key !== 'Escape') e.stopPropagation(); });
    const close = (v: SignedIn | null) => { shade.remove(); done(v); };
    (shade.querySelector('.ac-x') as HTMLElement).onclick = () => close(null);
    const fail = (m?: string) => { err.hidden = !m; err.textContent = m ?? ''; };
    const busy = (f: HTMLFormElement, on: boolean) => { (f.querySelector('.ac-go') as HTMLButtonElement).disabled = on; };
    let email = '';
    // the Google button (Google's own, in its frame); its answer signs in like a right code does
    const after = async (r: { error?: string; result?: SignedIn | 'both' }) => {
      if (r.error) { fail(r.error); return; }
      if (r.result !== 'both') { close(r.result!); return; }
      chooseLife();
    };
    const gbox = shade.querySelector('.ac-google') as HTMLElement;
    gbox.hidden = false; // (shown while Google draws its button: drawn hidden, it came out the wrong size)
    void googleButton(shade.querySelector('.ac-gbtn') as HTMLElement, async (credential) => { fail(); await after(await signInGoogle(credential)); })
      .then((ok) => { if (!ok) gbox.hidden = true; });
    fe.onsubmit = async (e) => {
      e.preventDefault(); fail(); busy(fe, true);
      email = ie.value.trim();
      const r = await askCode(email);
      busy(fe, false);
      if (r.error) { fail(r.error); return; }
      fc.querySelector('b')!.textContent = email;
      fe.hidden = true; fc.hidden = false;
      if (r.devCode) ic.value = r.devCode; // (a local server: the code is handed straight back)
      ic.focus();
    };
    (fc.querySelector('.ac-back') as HTMLElement).onclick = () => { fc.hidden = true; fe.hidden = false; fail(); ie.focus(); };
    fc.onsubmit = async (e) => {
      e.preventDefault(); fail(); busy(fc, true);
      const r = await signIn(email, ic.value);
      busy(fc, false);
      await after(r);
    };
    // one life per player: which one lives on
    const chooseLife = () => {
      fc.hidden = true; fe.hidden = true; (shade.querySelector('.ac-google') as HTMLElement).hidden = true;
      const pick = document.createElement('div');
      pick.className = 'ac-step';
      pick.innerHTML = `<p style="margin:0">This account already has a life, and so does this device. You live one life at a time: which one carries on?</p>
        <button class="ac-go" data-k="account">The account's life <small style="font-weight:400">(this device's is deleted)</small></button>
        <button class="ac-back" data-k="device" style="padding:10px;border:1px solid #4a3f33;border-radius:8px">This device's life <small>(the account's is replaced)</small></button>`;
      fc.after(pick);
      for (const b of pick.querySelectorAll<HTMLButtonElement>('button')) b.onclick = async () => {
        b.disabled = true;
        const v = await keepOne(b.dataset.k as 'account' | 'device');
        if (v) close(v); else { b.disabled = false; fail("That didn't go through. Try again."); }
      };
    };
    setTimeout(() => ie.focus(), 50);
  });
}

/** the game's Google sign-in client (public by design: Google checks it against this site's address) */
const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '930982452651-87jc4b5v4ucr869oqtbg0qr1aevg67mp.apps.googleusercontent.com';
type Gsi = { accounts: { id: { initialize(o: object): void; renderButton(el: HTMLElement, o: object): void } } };
let gsi: Promise<Gsi | null> | null = null;
/** Google's sign-in script, once (null: blocked or offline: the email code still works) */
function loadGsi(): Promise<Gsi | null> {
  gsi ??= new Promise((done) => {
    const w = window as unknown as { google?: Gsi };
    if (w.google?.accounts?.id) { done(w.google); return; }
    const sc = document.createElement('script');
    sc.src = 'https://accounts.google.com/gsi/client'; sc.async = true;
    sc.onload = () => done(w.google?.accounts?.id ? w.google : null);
    sc.onerror = () => { gsi = null; done(null); };
    document.head.appendChild(sc);
    setTimeout(() => done(w.google?.accounts?.id ? w.google : null), 8000);
  });
  return gsi;
}
/** Google's button in `el`; `on` gets its token. false: it isn't there (the email way only) */
async function googleButton(el: HTMLElement, on: (credential: string) => void): Promise<boolean> {
  const g = await loadGsi();
  if (!g || !el.isConnected) return false;
  try {
    g.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: (r: { credential?: string }) => { if (r.credential) on(r.credential); }, ux_mode: 'popup', auto_select: false, itp_support: true });
    g.accounts.id.renderButton(el, { type: 'standard', theme: 'filled_black', size: 'large', text: 'continue_with', shape: 'pill', logo_alignment: 'left', width: Math.min(340, Math.max(220, el.clientWidth || 300)) });
    return true;
  } catch { return false; }
}

let css = false;
function ensureCss() {
  if (css) return;
  css = true;
  const st = document.createElement('style');
  st.textContent = `
.ac-shade { position:fixed; inset:0; z-index:60; display:grid; place-items:center; background:rgba(8,6,10,.6); backdrop-filter:blur(3px); padding:16px; }
.ac-sheet { position:relative; width:min(380px, 100%); max-height:calc(100vh - 32px); overflow:auto; background:#1b1613; color:#f3ead9; border:1px solid rgba(243,234,217,.14); border-radius:12px; padding:20px 20px 16px; font:14px/1.45 'Segoe UI',system-ui,sans-serif; box-shadow:0 18px 50px rgba(0,0,0,.5); }
.ac-sheet h2 { margin:0 28px 6px 0; font:600 20px/1.2 Georgia,serif; }
.ac-intro { margin:0 0 14px; color:#d8cdbb; }
.ac-step { display:flex; flex-direction:column; gap:10px; }
.ac-google { display:flex; flex-direction:column; gap:12px; margin-bottom:12px; } .ac-google[hidden] { display:none; } .ac-gbtn { display:flex; justify-content:center; min-height:44px; }
.ac-or { display:flex; align-items:center; gap:10px; color:#8f8272; font-size:12px; } .ac-or::before, .ac-or::after { content:''; flex:1; height:1px; background:rgba(243,234,217,.14); }
.ac-step[hidden] { display:none; }
.ac-step label { display:flex; flex-direction:column; gap:6px; font-size:12px; color:#b9ab96; }
.ac-step input { font:16px 'Segoe UI',sans-serif; padding:10px 12px; border-radius:8px; border:1px solid #4a3f33; background:#2c241d; color:#f3ead9; outline:none; }
.ac-step input:focus { border-color:#e8a33d; }
.ac-code input { letter-spacing:.3em; font:600 20px monospace; }
.ac-go { padding:11px; border:0; border-radius:8px; background:#e8a33d; color:#1a140f; font:600 14px 'Segoe UI',sans-serif; cursor:pointer; }
.ac-go:disabled { opacity:.5; cursor:default; }
.ac-back { background:none; border:0; color:#b9ab96; font:12px 'Segoe UI',sans-serif; cursor:pointer; padding:2px; }
.ac-err { margin:10px 0 0; color:#ff9b88; }
.ac-err[hidden] { display:none; }
.ac-small { margin:12px 0 0; color:#8f8474; font-size:12px; }
.ac-x { position:absolute; top:10px; right:10px; width:30px; height:30px; border-radius:50%; border:0; background:rgba(255,255,255,.08); color:#f3ead9; font:18px/1 sans-serif; cursor:pointer; }
`;
  document.head.appendChild(st);
}
